import React, { ReactNode } from 'react';
import logo from '../assets/images/logo.svg';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';

/** Split layout shared by the sign-in page and the access status screens. */
const AuthShell: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t, language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-950 lg:flex-row">
      <aside className="relative overflow-hidden bg-primary-950 px-6 py-8 text-white lg:flex lg:w-[44%] lg:flex-col lg:justify-between lg:px-14 lg:py-12">
        {/* Two offset frames echo the logo: source window → destination window */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden lg:block">
          <div className="absolute -bottom-20 -right-28 h-64 w-96 rounded-[28px] border border-primary-400/25" />
          <div className="absolute -bottom-36 -right-10 h-64 w-96 rounded-[28px] border border-signal-400/40" />
        </div>

        <div className="relative flex items-center gap-3">
          <img src={logo} alt="" className="h-10 w-10 rounded-lg bg-white/95 p-1" />
          <span className="text-lg font-semibold tracking-tight">AppTransfer</span>
        </div>

        <div className="relative mt-8 hidden max-w-md lg:block">
          <p className="text-[40px] font-bold leading-[1.08] tracking-tight">{t('auth.heroTitle')}</p>
          <p className="mt-5 text-base leading-relaxed text-primary-200">{t('auth.heroText')}</p>
        </div>

        <p className="relative mt-3 text-sm text-primary-200 lg:mt-0">{t('auth.inviteOnly')}</p>
      </aside>

      <main className="flex flex-1 flex-col">
        <div className="flex justify-end gap-1 px-4 pt-4">
          <button
            type="button"
            onClick={() => setLanguage(language === 'hu' ? 'en' : 'hu')}
            className="btn btn-ghost btn-sm"
            aria-label={t('settings.language')}
          >
            {language === 'hu' ? 'English' : 'Magyar'}
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            className="btn btn-ghost btn-icon"
            aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
          >
            {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
          </button>
        </div>
        <div className="flex flex-1 items-center justify-center px-6 pb-16 pt-6">
          <div className="w-full max-w-sm">{children}</div>
        </div>
      </main>
    </div>
  );
};

export default AuthShell;
